/**
 * Scroll helper utilities for smooth scrolling and visibility management
 */

interface ScrollIntoViewOptions {
  behavior?: ScrollBehavior;
  block?: ScrollLogicalPosition;
  inline?: ScrollLogicalPosition;
}

/**
 * Scrolls an element into view within its container if it's not fully visible
 * @param element - The element to scroll into view
 * @param container - The scrollable container
 * @param options - Scroll behavior options
 */
export function scrollIntoViewIfNeeded(
  element: HTMLElement,
  container: HTMLElement,
  options: ScrollIntoViewOptions = { behavior: 'smooth', block: 'nearest' }
): void {
  const elementRect = element.getBoundingClientRect();
  const containerRect = container.getBoundingClientRect();

  // Check if element is already fully visible
  const isFullyVisible =
    elementRect.top >= containerRect.top &&
    elementRect.bottom <= containerRect.bottom;

  if (isFullyVisible) {
    return; // No need to scroll
  }

  // Calculate optimal scroll position
  const elementTop = element.offsetTop;
  const elementHeight = element.offsetHeight;
  const containerHeight = container.clientHeight;
  const currentScroll = container.scrollTop;

  let targetScroll = currentScroll;

  // If element is below viewport
  if (elementRect.bottom > containerRect.bottom) {
    // Scroll so bottom of element is visible
    targetScroll = elementTop + elementHeight - containerHeight;
  }
  
  // If element is above viewport
  if (elementRect.top < containerRect.top) {
    // Scroll so top of element is visible
    targetScroll = elementTop;
  }

  // Perform the scroll
  container.scrollTo({
    top: targetScroll,
    behavior: options.behavior || 'smooth',
  });
}

/**
 * Smoothly scrolls to show the top of an element
 * @param element - The element to scroll to
 * @param container - The scrollable container
 * @param offset - Optional offset from top (in pixels)
 */
export function scrollToElement(
  element: HTMLElement,
  container: HTMLElement,
  offset: number = 0
): void {
  const elementTop = element.offsetTop;
  
  container.scrollTo({
    top: elementTop - offset,
    behavior: 'smooth',
  });
}

